import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { X, Plus, Trash2, Save, RefreshCw, Check, Clock, XCircle, Wifi, WifiOff, Lock, LogOut, BarChart3, UtensilsCrossed, Image as ImageIcon, BellOff, CalendarOff } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import DashboardTab from './DashboardTab';
import MenuTab from './MenuTab';
import GalleryTab from './GalleryTab';
import BlockedSlotsTab from './BlockedSlotsTab';
import {
  getRestaurantInfo, updateRestaurantInfo,
  getChefSpecials, updateChefSpecial, createChefSpecial, deleteChefSpecial,
  getChefInfo, updateChefInfo,
  getTestimonials, deleteTestimonial,
  getReservations, updateReservationStatus, deleteReservation, dismissReservationNotification,
  getContactMessages, markMessageAsRead, deleteContactMessage,
  adminLogin, adminMe, adminLogout
} from '../services/api';

const AdminPanel = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // State for all data
  const [info, setInfo] = useState(null);
  const [specials, setSpecials] = useState([]);
  const [chef, setChef] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [messages, setMessages] = useState([]);

  // Cancellation UI state (which reservation is being cancelled + its reason text)
  const [cancellingId, setCancellingId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  // Confirmation UI state (which reservation is being confirmed + its custom message)
  const [confirmingId, setConfirmingId] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');

  // Reservations sort mode: 'recent' | 'upcoming' | 'past'
  const [reservationSort, setReservationSort] = useState('recent');

  // WebSocket connection for real-time updates
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws';
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setWsConnected(true);
        // Send ping every 30 seconds to keep connection alive
        const pingInterval = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, 30000);
        wsRef.current.pingInterval = pingInterval;
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleRealtimeMessage(message);
        } catch (e) {
          // Ignore non-JSON messages (like pong). Nothing to log; expected traffic.
          void e;
        }
      };
      
      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected');
        setWsConnected(false);
        if (wsRef.current?.pingInterval) {
          clearInterval(wsRef.current.pingInterval);
        }
        // Reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
      setWsConnected(false);
    }
  }, []);

  const handleRealtimeMessage = (message) => {
    switch (message.type) {
      case 'new_reservation':
        setReservations(prev => [message.data, ...prev]);
        toast({
          title: "🔔 Nueva Reservación",
          description: `${message.data.name} - ${message.data.date} a las ${message.data.time}`,
        });
        // Play notification sound
        playNotificationSound();
        break;
      
      case 'reservation_updated':
        setReservations(prev => prev.map(r => r.id === message.data.id ? message.data : r));
        break;
      
      case 'reservation_deleted':
        setReservations(prev => prev.filter(r => r.id !== message.data.id));
        break;
      
      case 'new_message':
        setMessages(prev => [message.data, ...prev]);
        toast({
          title: "✉️ Nuevo Mensaje",
          description: `De: ${message.data.name} - ${message.data.subject}`,
        });
        playNotificationSound();
        break;
      
      default:
        break;
    }
  };

  const playNotificationSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleOhEAQAASS0BAHwYAGB0BAA=');
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (e) {
      // Browser may block autoplay without user gesture; nothing to recover from.
      void e;
    }
  };

  // Check auth when panel opens
  useEffect(() => {
    if (isOpen) {
      checkAuth();
    } else {
      setAuthChecking(true);
    }
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        if (wsRef.current.pingInterval) {
          clearInterval(wsRef.current.pingInterval);
        }
        wsRef.current.close();
      }
    };
  }, [isOpen]);

  const checkAuth = async () => {
    setAuthChecking(true);
    try {
      await adminMe();
      setIsAuthenticated(true);
      connectWebSocket();
      fetchAllData();
    } catch {
      setIsAuthenticated(false);
      setLoading(false);
    } finally {
      setAuthChecking(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      await adminLogin(loginEmail, loginPassword);
      setIsAuthenticated(true);
      setLoginEmail('');
      setLoginPassword('');
      connectWebSocket();
      fetchAllData();
    } catch (err) {
      setLoginError(err.message || 'Error al iniciar sesión');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    adminLogout();
    setIsAuthenticated(false);
    if (wsRef.current) {
      wsRef.current.close();
    }
    onClose();
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [infoRes, specialsRes, chefRes, reviewsRes, reservationsRes, messagesRes] = await Promise.all([
        getRestaurantInfo(),
        getChefSpecials(),
        getChefInfo(),
        getTestimonials(),
        getReservations(reservationSort),
        getContactMessages()
      ]);
      setInfo(infoRes);
      setSpecials(specialsRes);
      setChef(chefRes);
      setReviews(reviewsRes);
      setReservations(reservationsRes);
      setMessages(messagesRes);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchReservations = useCallback(async (sort) => {
    try {
      const data = await getReservations(sort || reservationSort);
      setReservations(data);
    } catch (error) {
      console.error('Error fetching reservations:', error);
    }
  }, [reservationSort]);

  const handleReservationSortChange = (value) => {
    setReservationSort(value);
    fetchReservations(value);
  };

  const handleDismissNotification = async (id) => {
    try {
      await dismissReservationNotification(id);
      setReservations(prev => prev.map(r => r.id === id ? { ...r, dismissed: true } : r));
      toast({
        title: "Notificación descartada",
        description: "Puedes seguir confirmando o cancelando esta reservación cuando quieras.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo descartar la notificación",
        variant: "destructive",
      });
    }
  };

  if (!isOpen) return null;

  // Auth checking state
  if (authChecking) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-8 flex items-center gap-4">
          <RefreshCw className="w-6 h-6 animate-spin" />
          <span className="text-lg">Verificando acceso...</span>
        </div>
      </div>
    );
  }

  // Login form
  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
          <div className="p-8 text-center" style={{ backgroundColor: '#FFCCE0' }}>
            <Lock className="w-12 h-12 mx-auto mb-3 text-gray-700" />
            <h2 className="text-2xl font-bold text-gray-800" style={{ fontFamily: "'Crimson Text', serif" }}>
              Panel de Administración
            </h2>
            <p className="text-gray-600 mt-1 text-sm">La Vie en Rose</p>
          </div>
          <form onSubmit={handleLogin} className="p-8 space-y-5" data-testid="admin-login-form">
            {loginError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="login-error">
                {loginError}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="admin-email">Email</Label>
              <Input
                id="admin-email"
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="admin@lavieenrose.com"
                required
                data-testid="admin-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password">Contraseña</Label>
              <Input
                id="admin-password"
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="Ingrese su contraseña"
                required
                data-testid="admin-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-black hover:bg-gray-800 text-white"
              disabled={loginLoading}
              data-testid="admin-login-button"
            >
              {loginLoading ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Ingresando...</>
              ) : (
                'Iniciar Sesión'
              )}
            </Button>
          </form>
          <div className="px-8 pb-6 text-center">
            <Button variant="ghost" size="sm" onClick={onClose} className="text-gray-500">
              Volver al sitio
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleSaveInfo = async () => {
    setSaving(true);
    try {
      await updateRestaurantInfo(info);
      toast({
        title: "✅ Información Guardada",
        description: "La información del restaurante se ha actualizado correctamente."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo guardar la información",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSpecial = async (special) => {
    setSaving(true);
    try {
      await updateChefSpecial(special.id, {
        name: special.name,
        description: special.description,
        price: special.price,
        image: special.image,
        chef: special.chef
      });
      toast({
        title: "✅ Especialidad Guardada",
        description: `"${special.name}" se ha actualizado correctamente.`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo guardar la especialidad",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddSpecial = async () => {
    try {
      const newSpecial = await createChefSpecial({
        name: "Nueva Especialidad",
        description: "Descripción del plato",
        price: 0,
        image: "",
        chef: "Chef Vicenza"
      });
      setSpecials([...specials, newSpecial]);
      toast({
        title: "✅ Especialidad Agregada",
        description: "Nueva especialidad creada. Edita los detalles y guarda."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo crear la especialidad",
        variant: "destructive"
      });
    }
  };

  const handleDeleteSpecial = async (id) => {
    try {
      await deleteChefSpecial(id);
      setSpecials(specials.filter(s => s.id !== id));
      toast({
        title: "🗑️ Especialidad Eliminada",
        description: "La especialidad se ha eliminado correctamente."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar la especialidad",
        variant: "destructive"
      });
    }
  };

  const handleSaveChef = async () => {
    setSaving(true);
    try {
      await updateChefInfo(chef);
      toast({
        title: "✅ Información del Chef Guardada",
        description: "La información del chef se ha actualizado correctamente."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo guardar la información del chef",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTestimonial = async (id) => {
    try {
      await deleteTestimonial(id);
      setReviews(reviews.filter(r => r.id !== id));
      toast({
        title: "🗑️ Testimonio Eliminado",
        description: "El testimonio se ha eliminado correctamente."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el testimonio",
        variant: "destructive"
      });
    }
  };

  const handleUpdateReservationStatus = async (id, status, options = {}) => {
    try {
      await updateReservationStatus(id, status, options);
      setReservations(reservations.map(r => r.id === id ? { ...r, status } : r));
      toast({
        title: "✅ Reservación Actualizada",
        description: `Estado cambiado a: ${status === 'confirmed' ? 'Confirmada' : status === 'cancelled' ? 'Cancelada' : 'Pendiente'}`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo actualizar la reservación",
        variant: "destructive"
      });
    }
  };

  const handleStartCancel = (id) => {
    setCancellingId(id);
    setCancelReason('');
    setConfirmingId(null);
  };

  const handleCancelCancel = () => {
    setCancellingId(null);
    setCancelReason('');
  };

  const handleConfirmCancel = async (id) => {
    await handleUpdateReservationStatus(id, 'cancelled', { cancellation_reason: cancelReason.trim() || null });
    setCancellingId(null);
    setCancelReason('');
  };

  const handleStartConfirm = (id) => {
    setConfirmingId(id);
    setConfirmMessage('');
    setCancellingId(null);
  };

  const handleAbortConfirm = () => {
    setConfirmingId(null);
    setConfirmMessage('');
  };

  const handleFinalizeConfirm = async (id) => {
    await handleUpdateReservationStatus(id, 'confirmed', { confirmation_message: confirmMessage.trim() || null });
    setConfirmingId(null);
    setConfirmMessage('');
  };

  const handleDeleteReservation = async (id) => {
    try {
      await deleteReservation(id);
      setReservations(reservations.filter(r => r.id !== id));
      toast({
        title: "🗑️ Reservación Eliminada",
        description: "La reservación se ha eliminado correctamente."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar la reservación",
        variant: "destructive"
      });
    }
  };

  const handleMarkMessageRead = async (id) => {
    try {
      await markMessageAsRead(id);
      setMessages(messages.map(m => m.id === id ? { ...m, read: true } : m));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleDeleteMessage = async (id) => {
    try {
      await deleteContactMessage(id);
      setMessages(messages.filter(m => m.id !== id));
      toast({
        title: "🗑️ Mensaje Eliminado",
        description: "El mensaje se ha eliminado correctamente."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el mensaje",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <Badge className="bg-green-500 text-white"><Check className="w-3 h-3 mr-1" />Confirmada</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-500 text-white"><XCircle className="w-3 h-3 mr-1" />Cancelada</Badge>;
      default:
        return <Badge className="bg-yellow-500 text-white"><Clock className="w-3 h-3 mr-1" />Pendiente</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-8 flex items-center gap-4">
          <RefreshCw className="w-6 h-6 animate-spin" />
          <span className="text-lg">Cargando datos...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div className="flex items-center gap-4">
            <h2 className="text-3xl font-bold" style={{ fontFamily: "'Crimson Text', serif" }}>
              Panel de Administración
            </h2>
            <div className="flex items-center gap-2">
              {wsConnected ? (
                <Badge className="bg-green-500 text-white flex items-center gap-1">
                  <Wifi className="w-3 h-3" />
                  En vivo
                </Badge>
              ) : (
                <Badge variant="outline" className="text-gray-500 flex items-center gap-1">
                  <WifiOff className="w-3 h-3" />
                  Desconectado
                </Badge>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={fetchAllData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>
          <Button variant="ghost" onClick={onClose}>
            <X className="w-6 h-6" />
          </Button>
          <Button variant="outline" size="sm" onClick={handleLogout} className="text-red-600 border-red-200 hover:bg-red-50">
            <LogOut className="w-4 h-4 mr-2" />
            Salir
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <Tabs defaultValue="dashboard" className="w-full">
            <TabsList className="grid w-full grid-cols-10 mb-6">
              <TabsTrigger value="dashboard">
                <BarChart3 className="w-4 h-4 mr-1" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger value="reservations" className="relative">
                Reservaciones
                {reservations.filter(r => r.status === 'pending' && !r.dismissed).length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-testid="pending-reservations-badge">
                    {reservations.filter(r => r.status === 'pending' && !r.dismissed).length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="messages" className="relative">
                Mensajes
                {messages.filter(m => !m.read).length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {messages.filter(m => !m.read).length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="info">Información</TabsTrigger>
              <TabsTrigger value="menu" data-testid="menu-tab-trigger">
                <UtensilsCrossed className="w-4 h-4 mr-1" />
                Menú
              </TabsTrigger>
              <TabsTrigger value="gallery" data-testid="gallery-tab-trigger">
                <ImageIcon className="w-4 h-4 mr-1" />
                Galería
              </TabsTrigger>
              <TabsTrigger value="specials">Especialidades</TabsTrigger>
              <TabsTrigger value="chef">Chef</TabsTrigger>
              <TabsTrigger value="reviews">Reseñas</TabsTrigger>
              <TabsTrigger value="blocked" data-testid="blocked-tab-trigger">
                <CalendarOff className="w-4 h-4 mr-1" />
                Bloqueos
              </TabsTrigger>
            </TabsList>

            {/* Dashboard Tab */}
            <TabsContent value="dashboard">
              <DashboardTab reservations={reservations} messages={messages} />
            </TabsContent>

            {/* Menu Tab */}
            <TabsContent value="menu">
              <MenuTab />
            </TabsContent>

            {/* Gallery Tab */}
            <TabsContent value="gallery">
              <GalleryTab />
            </TabsContent>

            {/* Blocked dates Tab */}
            <TabsContent value="blocked">
              <BlockedSlotsTab />
            </TabsContent>

            {/* Reservations Tab */}
            <TabsContent value="reservations">
              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <CardTitle>Reservaciones</CardTitle>
                      <CardDescription>Gestiona las reservaciones de clientes</CardDescription>
                    </div>
                    <div className="flex gap-1 flex-wrap" role="group" aria-label="Ordenar reservaciones">
                      <Button
                        size="sm"
                        variant={reservationSort === 'recent' ? 'default' : 'outline'}
                        onClick={() => handleReservationSortChange('recent')}
                        data-testid="sort-recent"
                      >
                        Más recientes
                      </Button>
                      <Button
                        size="sm"
                        variant={reservationSort === 'upcoming' ? 'default' : 'outline'}
                        onClick={() => handleReservationSortChange('upcoming')}
                        data-testid="sort-upcoming"
                      >
                        Próximas
                      </Button>
                      <Button
                        size="sm"
                        variant={reservationSort === 'past' ? 'default' : 'outline'}
                        onClick={() => handleReservationSortChange('past')}
                        data-testid="sort-past"
                      >
                        Pasadas
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {reservations.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No hay reservaciones</p>
                  ) : (
                    <div className="space-y-4">
                      {reservations.map((reservation) => (
                        <div key={reservation.id} className="p-4 border rounded-lg">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="font-semibold text-lg">{reservation.name}</h4>
                              <p className="text-sm text-gray-500">{reservation.email} • {reservation.phone}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {getStatusBadge(reservation.status)}
                              {reservation.status === 'pending' && !reservation.dismissed && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                                  onClick={() => handleDismissNotification(reservation.id)}
                                  title="Descartar notificación (sin confirmar ni cancelar)"
                                  data-testid={`dismiss-notification-${reservation.id}`}
                                >
                                  <BellOff className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4 mb-3 text-sm">
                            <div><strong>Fecha:</strong> {reservation.date}</div>
                            <div><strong>Hora:</strong> {reservation.time}</div>
                            <div><strong>Invitados:</strong> {reservation.guests}</div>
                          </div>
                          {reservation.special_requests && (
                            <p className="text-sm text-gray-600 mb-3"><strong>Solicitudes:</strong> {reservation.special_requests}</p>
                          )}
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="bg-green-50 hover:bg-green-100 text-green-700"
                              onClick={() => handleStartConfirm(reservation.id)}
                              disabled={reservation.status === 'confirmed' || cancellingId === reservation.id || confirmingId === reservation.id}
                              data-testid={`confirm-reservation-${reservation.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />Confirmar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="bg-red-50 hover:bg-red-100 text-red-700"
                              onClick={() => handleStartCancel(reservation.id)}
                              disabled={reservation.status === 'cancelled' || cancellingId === reservation.id || confirmingId === reservation.id}
                              data-testid={`cancel-reservation-${reservation.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" />Cancelar
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteReservation(reservation.id)}
                              data-testid={`delete-reservation-${reservation.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          {confirmingId === reservation.id && (
                            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg space-y-2" data-testid={`confirm-form-${reservation.id}`}>
                              <Label className="text-sm font-medium text-green-900">
                                Mensaje personalizado de confirmación (opcional)
                              </Label>
                              <Textarea
                                value={confirmMessage}
                                onChange={(e) => setConfirmMessage(e.target.value)}
                                placeholder="Ej: ¡Gracias por su reservación! Hemos reservado la mesa junto a la ventana que solicitó..."
                                rows={3}
                                className="bg-white"
                                data-testid={`confirm-message-input-${reservation.id}`}
                              />
                              <p className="text-xs text-gray-500">
                                Este mensaje se incluirá en el correo de confirmación enviado al cliente. Si se deja vacío, se enviará el correo estándar con los detalles de la reservación.
                              </p>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => handleFinalizeConfirm(reservation.id)}
                                  data-testid={`finalize-confirm-${reservation.id}`}
                                >
                                  <Check className="w-4 h-4 mr-1" />Enviar confirmación
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handleAbortConfirm}
                                  data-testid={`abort-confirm-${reservation.id}`}
                                >
                                  Volver
                                </Button>
                              </div>
                            </div>
                          )}
                          {cancellingId === reservation.id && (
                            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg space-y-2" data-testid={`cancel-form-${reservation.id}`}>
                              <Label className="text-sm font-medium text-red-900">
                                Mensaje personalizado de cancelación (opcional)
                              </Label>
                              <Textarea
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                placeholder="Ej: Lamentamos informarle que debido a un evento privado no podremos recibirle esta fecha..."
                                rows={3}
                                className="bg-white"
                                data-testid={`cancel-reason-input-${reservation.id}`}
                              />
                              <p className="text-xs text-gray-500">
                                Este mensaje se incluirá en el correo de cancelación enviado al cliente. Si se deja vacío, se enviará el mensaje estándar.
                              </p>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  className="bg-red-600 hover:bg-red-700 text-white"
                                  onClick={() => handleConfirmCancel(reservation.id)}
                                  data-testid={`confirm-cancel-${reservation.id}`}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />Confirmar cancelación
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handleCancelCancel}
                                  data-testid={`abort-cancel-${reservation.id}`}
                                >
                                  Volver
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Messages Tab */}
            <TabsContent value="messages">
              <Card>
                <CardHeader>
                  <CardTitle>Mensajes de Contacto</CardTitle>
                  <CardDescription>Mensajes recibidos desde el formulario de contacto</CardDescription>
                </CardHeader>
                <CardContent>
                  {messages.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No hay mensajes</p>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((message) => (
                        <div 
                          key={message.id} 
                          className={`p-4 border rounded-lg ${!message.read ? 'bg-pink-50 border-pink-200' : ''}`}
                          onClick={() => !message.read && handleMarkMessageRead(message.id)}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-semibold">{message.name}</h4>
                              <p className="text-sm text-gray-500">{message.email}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {!message.read && <Badge className="bg-pink-500">Nuevo</Badge>}
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={(e) => { e.stopPropagation(); handleDeleteMessage(message.id); }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="font-medium text-gray-800">{message.subject}</p>
                          <p className="text-sm text-gray-600 mt-2">{message.message}</p>
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(message.created_at).toLocaleString('es-MX')}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Restaurant Info Tab */}
            <TabsContent value="info">
              {info && (
                <Card>
                  <CardHeader>
                    <CardTitle>Información del Restaurante</CardTitle>
                    <CardDescription>Edita la información general del restaurante</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nombre</Label>
                      <Input
                        value={info.name || ''}
                        onChange={(e) => setInfo({ ...info, name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Lema</Label>
                      <Input
                        value={info.tagline || ''}
                        onChange={(e) => setInfo({ ...info, tagline: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descripción</Label>
                      <Textarea
                        value={info.description || ''}
                        onChange={(e) => setInfo({ ...info, description: e.target.value })}
                        rows={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        value={info.email || ''}
                        onChange={(e) => setInfo({ ...info, email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Dirección</Label>
                      <Input
                        value={info.address || ''}
                        onChange={(e) => setInfo({ ...info, address: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Enlace Google Maps</Label>
                      <Input
                        value={info.mapsUrl || ''}
                        onChange={(e) => setInfo({ ...info, mapsUrl: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Horario</Label>
                      <Input
                        value={info.hours?.weekday || ''}
                        onChange={(e) => setInfo({ ...info, hours: { ...info.hours, weekday: e.target.value } })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Instagram URL</Label>
                        <Input
                          value={info.social?.instagram || ''}
                          onChange={(e) => setInfo({ ...info, social: { ...info.social, instagram: e.target.value } })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Facebook URL</Label>
                        <Input
                          value={info.social?.facebook || ''}
                          onChange={(e) => setInfo({ ...info, social: { ...info.social, facebook: e.target.value } })}
                        />
                      </div>
                    </div>
                    <Button onClick={handleSaveInfo} className="w-full bg-black hover:bg-gray-800" disabled={saving}>
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Guardando...' : 'Guardar Información'}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Chef's Specials Tab */}
            <TabsContent value="specials">
              <div className="space-y-4">
                {specials.map((special, index) => (
                  <Card key={special.id}>
                    <CardContent className="pt-6 space-y-3">
                      <div className="flex justify-between items-center">
                        <h4 className="font-semibold">Especialidad #{index + 1}</h4>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteSpecial(special.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <Input
                        placeholder="Nombre del plato"
                        value={special.name}
                        onChange={(e) => {
                          const newSpecials = [...specials];
                          newSpecials[index].name = e.target.value;
                          setSpecials(newSpecials);
                        }}
                      />
                      <Textarea
                        placeholder="Descripción"
                        value={special.description}
                        onChange={(e) => {
                          const newSpecials = [...specials];
                          newSpecials[index].description = e.target.value;
                          setSpecials(newSpecials);
                        }}
                        rows={3}
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          type="number"
                          placeholder="Precio"
                          value={special.price}
                          onChange={(e) => {
                            const newSpecials = [...specials];
                            newSpecials[index].price = parseFloat(e.target.value) || 0;
                            setSpecials(newSpecials);
                          }}
                        />
                        <Input
                          placeholder="Chef"
                          value={special.chef || ''}
                          onChange={(e) => {
                            const newSpecials = [...specials];
                            newSpecials[index].chef = e.target.value;
                            setSpecials(newSpecials);
                          }}
                        />
                      </div>
                      <Button 
                        onClick={() => handleSaveSpecial(special)} 
                        className="w-full bg-black hover:bg-gray-800"
                        disabled={saving}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Guardar Esta Especialidad
                      </Button>
                    </CardContent>
                  </Card>
                ))}
                <Button
                  variant="outline"
                  onClick={handleAddSpecial}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Nueva Especialidad
                </Button>
              </div>
            </TabsContent>

            {/* Chef Info Tab */}
            <TabsContent value="chef">
              {chef && (
                <Card>
                  <CardContent className="pt-6 space-y-4">
                    <Input
                      placeholder="Nombre del chef"
                      value={chef.name || ''}
                      onChange={(e) => setChef({ ...chef, name: e.target.value })}
                    />
                    <Input
                      placeholder="Título"
                      value={chef.title || ''}
                      onChange={(e) => setChef({ ...chef, title: e.target.value })}
                    />
                    <Textarea
                      placeholder="Biografía"
                      value={chef.bio || ''}
                      onChange={(e) => setChef({ ...chef, bio: e.target.value })}
                      rows={4}
                    />
                    <Input
                      placeholder="URL de la imagen"
                      value={chef.image || ''}
                      onChange={(e) => setChef({ ...chef, image: e.target.value })}
                    />
                    <div className="space-y-2">
                      <Label>Especialidades (separadas por coma)</Label>
                      <Input
                        placeholder="Especialidad 1, Especialidad 2, Especialidad 3"
                        value={chef.specialties?.join(', ') || ''}
                        onChange={(e) => setChef({ ...chef, specialties: e.target.value.split(',').map(s => s.trim()) })}
                      />
                    </div>
                    <Button onClick={handleSaveChef} className="w-full bg-black hover:bg-gray-800" disabled={saving}>
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Guardando...' : 'Guardar Información del Chef'}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Reviews Tab */}
            <TabsContent value="reviews">
              <Card>
                <CardHeader>
                  <CardTitle>Testimonios</CardTitle>
                  <CardDescription>Gestiona los testimonios de clientes</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.id} className="p-4 border rounded-lg flex justify-between items-start">
                      <div>
                        <p className="font-semibold">{review.name}</p>
                        <p className="text-sm text-gray-600 mt-1">{review.comment}</p>
                        <p className="text-xs text-gray-400 mt-2">⭐ {review.rating}/5 • {review.date}</p>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteTestimonial(review.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
